/* Native enhancements only. Commerce remains in guf.js. */
(() => {
  'use strict';
  const script = document.getElementById('GufGalleryScript');
  if (!script) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileTabs = window.matchMedia('(max-width: 749px)');
  const level = script.dataset.level || 'full';
  const scopes = new Map();
  const allowed = () => !reduced.matches && level !== 'off' && !document.body.classList.contains('motion-off');
  const full = () => allowed() && level === 'full';
  let progress;
  let progressFrame;
  const cleanupFns = [];
  function on(node, type, fn, cleanup, options) {
    node.addEventListener(type, fn, options);
    cleanup.push(() => node.removeEventListener(type, fn, options));
  }
  function initialize(section) {
    if (scopes.has(section)) return;
    const cleanup = [];
    scopes.set(section, cleanup);
    const find = selector => [...section.querySelectorAll(selector)];
    // Only add hidden reveal states after an observer is successfully attached.
    if (allowed() && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('gg-revealed');
          observer.unobserve(entry.target);
        });
      }, { threshold: .08, rootMargin: '0px 0px 30px 0px' });
      find('[data-gg-reveal]').forEach(node => {
        observer.observe(node);
        node.classList.add('gg-will-reveal');
      });
      cleanup.push(() => { observer.disconnect(); find('[data-gg-reveal]').forEach(node => node.classList.remove('gg-will-reveal')); });
    }
    find('[data-gg-carousel]').forEach(carousel => {
      const rail = carousel.querySelector('[data-gg-rail]');
      const prev = carousel.querySelector('[data-gg-prev]');
      const next = carousel.querySelector('[data-gg-next]');
      const controls = carousel.querySelector('.gg-rail-controls');
      if (!rail || !prev || !next) return;
      let frame;
      function update() {
        const max = rail.scrollWidth - rail.clientWidth;
        const position = Math.abs(rail.scrollLeft);
        prev.disabled = position <= 2;
        next.disabled = position >= max - 2;
        if (controls) controls.hidden = max <= 2;
      }
      function move(direction) {
        const card = rail.querySelector('[data-gg-card]');
        const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
        const width = card?.getBoundingClientRect().width || rail.clientWidth;
        const rtl = getComputedStyle(rail).direction === 'rtl' ? -1 : 1;
        rail.scrollBy({left: direction * rtl * (width + gap), behavior: allowed() ? 'smooth' : 'auto'});
      }
      on(prev, 'click', () => move(-1), cleanup);
      on(next, 'click', () => move(1), cleanup);
      on(rail, 'scroll', () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(update); }, cleanup, {passive: true});
      on(rail, 'keydown', event => {
        if (event.target !== rail || !['ArrowRight', 'ArrowLeft'].includes(event.key)) return;
        event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1);
      }, cleanup);
      if ('ResizeObserver' in window) {
        const resize = new ResizeObserver(update); resize.observe(rail); cleanup.push(() => resize.disconnect());
      } else on(window, 'resize', update, cleanup, {passive: true});
      update(); cleanup.push(() => cancelAnimationFrame(frame));
    });
    find('[data-gg-card]').forEach(card => {
      const primary = card.querySelector('.gg-card-primary');
      const thumbs = [...card.querySelectorAll('[data-gg-thumb]')];
      if (!primary || !thumbs.length) return;
      const original = {src:primary.getAttribute('src'), srcset:primary.getAttribute('srcset'), alt:primary.alt};
      thumbs.forEach((thumb, index) => on(thumb, 'click', () => {
        thumbs.forEach(item => item.setAttribute('aria-pressed', String(item === thumb)));
        if (index === 0) {
          primary.src = original.src; primary.alt = original.alt;
          if (original.srcset) primary.setAttribute('srcset', original.srcset);
          card.removeAttribute('data-gg-custom-image');
        } else {
          primary.removeAttribute('srcset'); primary.src = thumb.dataset.src; primary.alt = thumb.dataset.alt || '';
          card.dataset.ggCustomImage = '';
        }
      }, cleanup));
    });
    find('[data-gg-tabs]').forEach(studio => {
      const list = studio.querySelector('[data-gg-tablist]');
      const tabs = [...studio.querySelectorAll('[data-gg-tab]')];
      const panels = [...studio.querySelectorAll('[data-gg-tabpanel]')];
      if (!list || !tabs.length || tabs.length !== panels.length) return;
      let active = 0;
      function orientation() { list.setAttribute('aria-orientation', mobileTabs.matches ? 'horizontal' : 'vertical'); }
      function activate(index, focus = false) {
        active = (index + tabs.length) % tabs.length;
        tabs.forEach((tab, i) => {
          tab.setAttribute('aria-selected', String(i === active)); tab.tabIndex = i === active ? 0 : -1;
          panels[i].hidden = i !== active;
          if (i !== active) panels[i].querySelectorAll('video').forEach(video => video.pause());
        });
        if (focus) tabs[active].focus({preventScroll: true});
        if (allowed() && typeof panels[active].animate === 'function') {
          panels[active].getAnimations().forEach(animation => animation.cancel());
          panels[active].animate([{opacity: .25, transform: full() ? 'translateY(12px)' : 'none'}, {opacity: 1, transform: 'none'}], {duration: full() ? 420 : 180, easing: 'cubic-bezier(.22,.61,.36,1)'});
        }
        if (mobileTabs.matches && focus) {
          const target = tabs[active];
          const offset = target.offsetLeft - list.offsetLeft - (list.clientWidth - target.offsetWidth) / 2;
          list.scrollTo({left: offset, behavior: allowed() ? 'smooth' : 'auto'});
        }
      }
      list.setAttribute('role', 'tablist'); orientation();
      studio.classList.add('gg-tabs-ready');
      tabs.forEach((tab, index) => {
        tab.setAttribute('role', 'tab'); tab.setAttribute('aria-controls', panels[index].id);
        panels[index].setAttribute('role', 'tabpanel'); panels[index].tabIndex = 0;
        on(tab, 'click', event => { event.preventDefault(); activate(index); }, cleanup);
        on(tab, 'keydown', event => {
          let indexToSelect;
          if (event.key === 'Home') indexToSelect = 0;
          else if (event.key === 'End') indexToSelect = tabs.length - 1;
          else if (event.key === (mobileTabs.matches ? 'ArrowRight' : 'ArrowDown')) indexToSelect = active + 1;
          else if (event.key === (mobileTabs.matches ? 'ArrowLeft' : 'ArrowUp')) indexToSelect = active - 1;
          else if (event.key === ' ') indexToSelect = index;
          if (indexToSelect === undefined) return;
          event.preventDefault(); activate(indexToSelect, true);
        }, cleanup);
      });
      on(mobileTabs, 'change', orientation, cleanup);
      on(studio, 'shopify:block:select', event => {
        const selected = panels.findIndex(panel => panel.dataset.blockId === event.detail?.blockId || panel.contains(event.target));
        if (selected >= 0) activate(selected);
      }, cleanup);
      activate(0);
      cleanup.push(() => panels.forEach(panel => panel.querySelectorAll('video').forEach(video => video.pause())));
    });
    find('[data-gg-marquee]').forEach(ticker => {
      const toggle = ticker.querySelector('[data-gg-marquee-toggle]');
      let paused = false;
      let visible = false;
      function sync() {
        const running = full() && visible && !paused && !document.hidden;
        ticker.classList.toggle('gg-running', running);
        ticker.classList.toggle('gg-paused', paused);
        if (toggle) {
          toggle.hidden = !full();
          toggle.setAttribute('aria-pressed', String(paused));
          toggle.setAttribute('aria-label', paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel);
          const icon = toggle.querySelector('[data-gg-ticker-icon]'); if (icon) icon.textContent = paused ? '▶' : 'Ⅱ';
        }
      }
      if (toggle) on(toggle, 'click', () => { paused = !paused; sync(); }, cleanup);
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; sync(); });
        observer.observe(ticker); cleanup.push(() => observer.disconnect());
      }
      on(document, 'visibilitychange', sync, cleanup); on(reduced, 'change', sync, cleanup); sync();
    });
    // Keep the poster visible until actual playback starts, including autoplay rejection.
    find('.gg-hero-background video').forEach(video => {
      const sync = () => video.toggleAttribute('data-gg-playing', !video.paused);
      on(video, 'play', sync, cleanup); on(video, 'pause', sync, cleanup); on(video, 'ended', sync, cleanup); sync();
    });
  }
  function destroy(section) {
    scopes.get(section)?.forEach(dispose => dispose()); scopes.delete(section);
  }
  function syncPreferences() {
    document.body.classList.toggle('gg-motion-off', !allowed());
    document.body.classList.toggle('gg-motion-subtle', allowed() && level === 'subtle');
    if (!allowed()) {
      document.querySelectorAll('.gg-will-reveal').forEach(node => node.classList.add('gg-revealed'));
      document.querySelectorAll('[data-gg-tabpanel]').forEach(panel => panel.getAnimations?.().forEach(animation => animation.cancel()));
    }
    if (progress) progress.hidden = !full();
  }
  function boot(scope = document) { scope.querySelectorAll('.shopify-section').forEach(initialize); }
  syncPreferences(); boot();
  if (script.dataset.progress === 'true' && document.body.classList.contains('template-index')) {
    progress = document.createElement('div'); progress.className = 'gg-progress'; progress.setAttribute('aria-hidden', 'true'); document.body.append(progress);
    const update = () => {
      const height = document.documentElement.scrollHeight - window.innerHeight;
      progress.style.transform = `scaleX(${height > 0 ? Math.max(0, Math.min(1, window.scrollY / height)) : 0})`;
    };
    on(window, 'scroll', () => { cancelAnimationFrame(progressFrame); progressFrame = requestAnimationFrame(update); }, cleanupFns, {passive:true});
    on(window, 'resize', update, cleanupFns, {passive:true}); update(); syncPreferences();
  }
  on(reduced, 'change', syncPreferences, cleanupFns);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  window.addEventListener('pagehide', event => {
    if (event.persisted) return;
    [...scopes.keys()].forEach(destroy); cleanupFns.forEach(dispose => dispose()); cancelAnimationFrame(progressFrame);
  });
})();
